import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { ExperienceStatus, getMoodsIds, getTranslatedName, moods } from '@utriper/core';
import { CharacterCounter, NumberInput, SelectCheckbox, SelectComponent, Switch, Textarea, TextInput } from '@utriper/core-ui';
import { Container, Label, Title } from '../../styles';
import { FormHelperText, Grid2 } from '@mui/material';

const DetailsForm = ({ form, submitted = false }) => {
    const { values, errors } = useFormState();
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const statusOptions = useMemo(
        () =>
            ExperienceStatus.getIds().map((id) => ({
                id,
                name: ExperienceStatus.getTranslatedName(ExperienceStatus.status, id, i18n.language),
            })),
        [i18n.language]
    );

    const modusesOptions = useMemo(
        () =>
            getMoodsIds().map((mood) => ({
                id: mood,
                name: getTranslatedName(moods, mood, i18n.language),
            })),
        [i18n.language]
    );

    return (
        <React.Fragment>
            <Title title={translate('details.title')} description={translate('details.description')} />

            <Grid2 container size={12} spacing={1}>
                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    {/* Name */}
                    <Container>
                        <Label>{translate('name')}</Label>
                        <Field
                            name="name"
                            component={TextInput}
                            placeholder={translate('name_placeholder')}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        maxLength: 255,
                                    },
                                },
                            }}
                        />
                    </Container>

                    {/* Description1 (subtitle) */}
                    <Container>
                        <Label>{translate('subtitle')}</Label>
                        <Field
                            name="description1"
                            component={TextInput}
                            placeholder={translate('subtitle_placeholder')}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        maxLength: 255,
                                    },
                                },
                            }}
                        />
                    </Container>

                    {/* Description2 (description) */}
                    <Container>
                        <Label>{translate('description')}</Label>
                        <Field
                            name="description2"
                            component={Textarea}
                            placeholder={translate('description_placeholder')}
                            minRows={4}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        maxLength: 3000,
                                    },
                                },
                            }}
                        />
                        <CharacterCounter length={values?.description2?.length ?? 0} maxLength={3000} />
                    </Container>
                </Grid2>

                <Grid2 container size={{ xs: 12, md: 6 }} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    {/* Status */}
                    <Container>
                        <Label>{translate('status')}</Label>
                        <Field name="status" component={SelectComponent} values={statusOptions} />
                    </Container>

                    {/* Moduses */}
                    <Container>
                        <Label>{translate('moods')}</Label>
                        <Field
                            name="moduses"
                            component={SelectCheckbox}
                            options={modusesOptions}
                            values={values?.moduses ?? []}
                            label={translate('moods_placeholder')}
                            hasError={submitted && errors?.moduses}
                            onChange={(newValues) => {
                                form.change('moduses', [...newValues]);
                            }}
                        />
                        {submitted && errors?.moduses && <FormHelperText error>{errors.moduses}</FormHelperText>}
                    </Container>

                    {/* Duration */}
                    <Container>
                        <Label>{translate('duration')}</Label>
                        <Field name="duration" component={NumberInput} placeholder={translate('duration_placeholder')} disabled={!values.allowOpenDateBooking} />
                        {!values.allowOpenDateBooking && <FormHelperText>{translate('alert_allow_open_date_booking')}</FormHelperText>}
                    </Container>

                    {/* Require ID */}
                    <Container>
                        <Field name="requireId" type="checkbox" component={Switch} label={translate('check_in')} />
                    </Container>

                    {/* Time Period */}
                    <Container>
                        {/* <Label>{translate('time_period_label')}</Label> */}
                        <Field id="timePeriod" name="timePeriod" type="checkbox" component={Switch} label={translate('time_period_label')} />
                        <FormHelperText>{translate('time_period_helper')}</FormHelperText>
                    </Container>
                </Grid2>
            </Grid2>
        </React.Fragment>
    );
};

export default DetailsForm;
